import { useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem,
  Alert, Typography, Autocomplete, Checkbox, FormControlLabel, Paper, Chip, IconButton, Collapse,
} from '@mui/material'
import { ExpandMore, ExpandLess, Add } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  listSupplierArticles, addSupplierArticle, addSupplierArticlePrice, type SupplierArticle,
} from '../../api/furnizori'
import { listArticles, listUnits } from '../../api/nomenclatoare'
import { errorMessage } from '../../api/client'
import type { SupplierListItem } from '../../api/furnizori'

/** FR-MDF-003, 004, 006 — articolele furnizate de un furnizor, cu preț negociat și mapping de cod. */
export default function SupplierArticlesDialog({ supplier, onClose }: { supplier: SupplierListItem | null; onClose: () => void }) {
  const open = supplier !== null
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)

  const { data: items } = useQuery({
    queryKey: ['achizitii', 'furnizor-articole', supplier?.id],
    queryFn: () => listSupplierArticles(supplier!.id),
    enabled: open,
  })
  const { data: articles } = useQuery({ queryKey: ['nomenclatoare', 'articole', 'toate'], queryFn: () => listArticles(), enabled: open })
  const { data: units } = useQuery({ queryKey: ['nomenclatoare', 'um'], queryFn: listUnits, enabled: open })

  const [newArticleId, setNewArticleId] = useState<string | null>(null)
  const [newCode, setNewCode] = useState('')
  const [newDefault, setNewDefault] = useState(false)

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['achizitii', 'furnizor-articole', supplier?.id] })

  const addMutation = useMutation({
    mutationFn: () => addSupplierArticle(supplier!.id, { articleId: newArticleId!, supplierArticleCode: newCode || null, isDefault: newDefault }),
    onSuccess: () => { setNewArticleId(null); setNewCode(''); setNewDefault(false); invalidate() },
    onError: (e) => setError(errorMessage(e)),
  })

  const availableArticles = articles?.filter((a) => !items?.some((i) => i.articleId === a.id)) ?? []

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Articole furnizate — {supplier?.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
            <Autocomplete
              sx={{ flexGrow: 1 }} options={availableArticles.map((a) => a.id)}
              getOptionLabel={(id) => { const a = availableArticles.find((x) => x.id === id); return a ? `${a.code} — ${a.shortDescription}` : id }}
              value={newArticleId} onChange={(_, v) => setNewArticleId(v)}
              renderInput={(params) => <TextField {...params} label="Articol nou" size="small" />}
            />
            <TextField size="small" label="Cod la furnizor" value={newCode} onChange={(e) => setNewCode(e.target.value)} sx={{ width: 160 }} />
            <FormControlLabel sx={{ whiteSpace: 'nowrap' }} control={<Checkbox checked={newDefault} onChange={(e) => setNewDefault(e.target.checked)} />} label="Implicit" />
            <Button variant="outlined" startIcon={<Add />} disabled={!newArticleId || addMutation.isPending} onClick={() => addMutation.mutate()}>
              Adaugă
            </Button>
          </Stack>

          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

          <Stack spacing={1}>
            {items?.length === 0 && <Typography variant="body2" color="text.secondary">Niciun articol asociat încă.</Typography>}
            {items?.map((sa) => (
              <SupplierArticleRow key={sa.id} sa={sa} units={units ?? []}
                expanded={expanded === sa.id} onToggle={() => setExpanded(expanded === sa.id ? null : sa.id)}
                onPriceAdded={invalidate} />
            ))}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Închide</Button>
      </DialogActions>
    </Dialog>
  )
}

function SupplierArticleRow({ sa, units, expanded, onToggle, onPriceAdded }: {
  sa: SupplierArticle; units: { id: string; code: string; name: string }[]
  expanded: boolean; onToggle: () => void; onPriceAdded: () => void
}) {
  const [price, setPrice] = useState('')
  const [currency, setCurrency] = useState('EUR')
  const [unitId, setUnitId] = useState('')
  const [validFrom, setValidFrom] = useState('')
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => addSupplierArticlePrice(sa.id, { price: Number(price), currency, unitOfMeasureId: unitId, validFrom }),
    onSuccess: () => { setPrice(''); setValidFrom(''); onPriceAdded() },
    onError: (e) => setError(errorMessage(e)),
  })

  const currentPrice = sa.prices[0]

  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
        <Stack sx={{ flexGrow: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {sa.articleCode} — {sa.articleDescription} {sa.isDefault && <Chip label="implicit" size="small" sx={{ ml: 1, height: 18, fontSize: 10 }} />}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Cod furnizor: {sa.supplierArticleCode ?? '—'} · Preț curent: {currentPrice ? `${currentPrice.price} ${currentPrice.currency}/${currentPrice.unitOfMeasureCode} (din ${currentPrice.validFrom})` : '—'}
          </Typography>
        </Stack>
        <IconButton size="small" onClick={onToggle}>{expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}</IconButton>
      </Stack>
      <Collapse in={expanded}>
        <Stack direction="row" spacing={1.5} sx={{ mt: 1.5, alignItems: 'flex-start' }}>
          <TextField size="small" type="number" label="Preț" value={price} onChange={(e) => setPrice(e.target.value)} sx={{ width: 110 }} />
          <TextField size="small" label="Monedă" value={currency} onChange={(e) => setCurrency(e.target.value)} sx={{ width: 90 }} />
          <TextField size="small" select label="UM" value={unitId} onChange={(e) => setUnitId(e.target.value)} sx={{ width: 130 }}>
            {units.map((u) => <MenuItem key={u.id} value={u.id}>{u.code}</MenuItem>)}
          </TextField>
          <TextField size="small" type="date" label="Valabil de la" value={validFrom} onChange={(e) => setValidFrom(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }} sx={{ width: 160 }} />
          <Button size="small" variant="outlined" disabled={!price || !unitId || !validFrom || mutation.isPending} onClick={() => mutation.mutate()}>
            Adaugă preț
          </Button>
        </Stack>
        {error && <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>{error}</Alert>}
      </Collapse>
    </Paper>
  )
}
